'use client';
import createContextHardened from '@/helper/createContextHardened';
import { WSP_DEBUG } from '@/helper/errors';
import useStableCallback from '@/helper/useStableCallback';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export type WebSocketOpenListener = () => void;
export type WebSocketMessageListener = (event: MessageEvent<string>) => void;

/**
 * Reconnect delays with capped backoff. The first retry is delayed on purpose:
 * an immediate reconnect to the same port can fail due to TCP TIME_WAIT.
 *
 * Two ladders — a lost connection is retried aggressively, but if none ever
 * succeeded the target most likely is not installed at all, and every failed
 * attempt costs an unsuppressible browser console error. Tab-return/online
 * events and the status page's retry reset the backoff.
 */
const RECONNECT_DELAYS_MS = [2000, 5000, 10000, 20000, 30000];
const RECONNECT_DELAYS_NEVER_CONNECTED_MS = [2000, 5000, 15000, 60000, 300000];

interface WebSocketContextState {
  connected: boolean;
  connecting: boolean;
  /** true once a connection has succeeded at least once in this provider's lifetime */
  connectedOnce: boolean;
  /**
   * True once a connection attempt has failed, never reset again. Lets a UI
   * tell the first attempt (nothing known yet) from every later one, so it can
   * stay stable across retries instead of flickering with each attempt.
   */
  attemptFailed: boolean;
  /** triggers an immediate attempt and resets the backoff; the reconnect loop handles failures */
  connect: () => void;
  disconnect: () => void;
  send: (data: string) => boolean;
  addOnOpenListener: (listener: WebSocketOpenListener) => void;
  removeOnOpenListener: (listener: WebSocketOpenListener) => void;
  addOnMessageListener: (listener: WebSocketMessageListener) => void;
  removeOnMessageListener: (listener: WebSocketMessageListener) => void;
}

const [WebSocketContext, useWebSocket] = createContextHardened<WebSocketContextState>('WebSocket');
export { useWebSocket };

interface WebSocketProviderProps {
  children: React.ReactNode;
  hostname: string;
  port: number;
}

export function WebSocketProvider({ children, hostname, port }: WebSocketProviderProps): React.JSX.Element {
  const [connected, setConnected] = useState<WebSocketContextState['connected']>(false);
  const [connecting, setConnecting] = useState<WebSocketContextState['connecting']>(false);
  const [attemptFailed, setAttemptFailed] = useState<WebSocketContextState['attemptFailed']>(false);
  const [connectedOnce, setConnectedOnce] = useState<WebSocketContextState['connectedOnce']>(false);

  /** the one and only socket, null while none exists */
  const ws = useRef<WebSocket | null>(null);
  /** true while the last close was requested via disconnect() or unmount, suppresses auto-reconnect */
  const intentionalCloseRef = useRef<boolean>(false);
  /**
   * Same fact as `connectedOnce`, deliberately duplicated as a ref: the backoff
   * ladder is picked in `onclose`, which can run in the same tick as `onopen`.
   * A state read there would still be `false` and would slow the retry down for
   * a connection that demonstrably worked.
   */
  const everConnectedRef = useRef<boolean>(false);
  /**
   * How many attempts have been scheduled since the last success — the index
   * into the ladder above. A ref, not state: it is read and written inside
   * `scheduleReconnect`, which runs from `onclose` and can fire repeatedly
   * before React re-renders, where a state read would keep returning the old
   * count and the backoff would never advance.
   */
  const reconnectAttemptCountRef = useRef<number>(0);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  /**
   * Listeners live here rather than on the socket itself, so they survive every
   * socket instance this provider creates and destroys.
   */
  const onOpenListenersRef = useRef<Set<WebSocketOpenListener>>(new Set());
  const onMessageListenersRef = useRef<Set<WebSocketMessageListener>>(new Set());

  // Each listener is isolated: one that throws must not starve the rest.
  const notifyOnOpenListeners = useCallback((): void => {
    onOpenListenersRef.current.forEach((listener) => {
      try {
        listener();
      } catch (e) {
        console.error('websocket open listener failed', e);
      }
    });
  }, []);

  const notifyOnMessageListeners = useCallback((event: MessageEvent<string>): void => {
    onMessageListenersRef.current.forEach((listener) => {
      try {
        listener(event);
      } catch (e) {
        console.error('websocket message listener failed', e);
      }
    });
  }, []);

  const clearReconnectTimer = useCallback((): void => {
    if (reconnectTimerRef.current !== undefined) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = undefined;
    }
  }, []);

  const detachSocket = useCallback((socket: WebSocket): void => {
    socket.onopen = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
  }, []);

  const openSocket = useStableCallback((): void => {
    clearReconnectTimer();

    const existing = ws.current;
    if (existing !== null) {
      // an open or still-connecting socket is already the one we want, so this
      // is where a second socket is prevented
      if (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING) {
        return;
      }
      detachSocket(existing);
      existing.close();
      ws.current = null;
    }

    setConnecting(true);

    let socket: WebSocket;

    try {
      socket = new WebSocket(`ws://${hostname}:${port}`);
    } catch {
      setConnecting(false);
      setAttemptFailed(true);
      scheduleReconnect();
      return;
    }

    ws.current = socket;
    let opened = false;

    socket.onopen = () => {
      if (ws.current !== socket) {
        return;
      }

      if (WSP_DEBUG) {
        console.log('websocket "' + hostname + ':' + port + '" connected');
      }

      opened = true;
      everConnectedRef.current = true;
      setConnectedOnce(true);
      reconnectAttemptCountRef.current = 0;
      setConnecting(false);
      setConnected(true);
      notifyOnOpenListeners();
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      if (ws.current !== socket) {
        return;
      }
      notifyOnMessageListeners(event);
    };

    socket.onclose = (event: CloseEvent) => {
      if (ws.current !== socket) {
        return;
      }

      if (WSP_DEBUG) {
        console.log('websocket "' + hostname + ':' + port + '" closed (code ' + event.code + ')');
      }

      ws.current = null;
      setConnecting(false);
      setConnected(false);

      if (!intentionalCloseRef.current) {
        scheduleReconnect();
      }

      // a close we asked for is not a failed attempt: only an unsolicited close
      // before the socket ever opened means the target is unreachable
      if (!opened && !intentionalCloseRef.current) {
        setAttemptFailed(true);
      }
    };

    // A close event always follows an error event, so reconnect scheduling
    // happens in onclose only.
    socket.onerror = () => {
      if (WSP_DEBUG) {
        console.log('websocket "' + hostname + ':' + port + '" connection error');
      }
    };
  }, [hostname, port, clearReconnectTimer, detachSocket]);

  const scheduleReconnect = useStableCallback((): void => {
    if (intentionalCloseRef.current || reconnectTimerRef.current !== undefined) {
      return;
    }

    const delays = everConnectedRef.current ? RECONNECT_DELAYS_MS : RECONNECT_DELAYS_NEVER_CONNECTED_MS;
    const attempt = reconnectAttemptCountRef.current;
    const delay = delays[Math.min(attempt, delays.length - 1)] + Math.floor(Math.random() * 1000);
    reconnectAttemptCountRef.current = attempt + 1;

    if (WSP_DEBUG) {
      console.log('websocket reconnect attempt ' + (attempt + 1) + ' scheduled in ' + delay + 'ms');
    }

    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = undefined;
      openSocket();
    }, delay);
  }, [openSocket]);

  // the socket itself stays inside the provider: callers get the connection
  // state and the send/listener methods, never the WebSocket object
  const connect = useCallback((): void => {
    intentionalCloseRef.current = false;
    reconnectAttemptCountRef.current = 0;
    openSocket();
  }, [openSocket]);

  const disconnect = useCallback((): void => {
    if (WSP_DEBUG) {
      console.log('disconnecting websocket "' + hostname + ':' + port + '"');
    }

    intentionalCloseRef.current = true;
    clearReconnectTimer();
    reconnectAttemptCountRef.current = 0;

    const socket = ws.current;
    if (socket !== null) {
      socket.close();
    } else {
      setConnected(false);
      setConnecting(false);
    }
  }, [hostname, port, clearReconnectTimer]);

  const send = useCallback((data: string): boolean => {
    const socket = ws.current;

    if (socket === null || socket.readyState !== WebSocket.OPEN) {
      if (WSP_DEBUG) {
        console.log('websocket send skipped, socket not open');
      }
      return false;
    }

    socket.send(data);
    return true;
  }, []);

  const handleBrowserEvent = useStableCallback(
    (eventType: 'visibility' | 'online'): void => {
      if (eventType === 'visibility' && document.visibilityState !== 'visible') {
        return;
      }

      if (intentionalCloseRef.current) {
        return;
      }

      const socket = ws.current;
      if (socket !== null && socket.readyState === WebSocket.OPEN) {
        return;
      }

      if (WSP_DEBUG) {
        console.log('websocket reconnecting due to browser event: ' + eventType);
      }

      reconnectAttemptCountRef.current = 0;
      openSocket();
    },
    [openSocket],
  );

  useEffect(() => {
    const onVisibilityChange = (): void => handleBrowserEvent('visibility');
    const onOnline = (): void => handleBrowserEvent('online');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);

      intentionalCloseRef.current = true;
      clearReconnectTimer();
      if (ws.current !== null) {
        detachSocket(ws.current);
        ws.current.close();
        ws.current = null;
      }
    };
  }, [handleBrowserEvent, clearReconnectTimer, detachSocket]);

  const addOnOpenListener = useCallback((listener: WebSocketOpenListener): void => {
    onOpenListenersRef.current.add(listener);
  }, []);

  const removeOnOpenListener = useCallback((listener: WebSocketOpenListener): void => {
    onOpenListenersRef.current.delete(listener);
  }, []);

  const addOnMessageListener = useCallback((listener: WebSocketMessageListener): void => {
    onMessageListenersRef.current.add(listener);
  }, []);

  const removeOnMessageListener = useCallback((listener: WebSocketMessageListener): void => {
    onMessageListenersRef.current.delete(listener);
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        connected,
        connecting,
        connectedOnce,
        attemptFailed,
        connect,
        disconnect,
        send,
        addOnOpenListener,
        removeOnOpenListener,
        addOnMessageListener,
        removeOnMessageListener,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}
